import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { PageRenderProfile } from '@/types/enums';

export function PageScrollContainer({
  children,
  isDraftPreview = false,
  renderProfile = PageRenderProfile.Standard
}: {
  children: ReactNode;
  isDraftPreview?: boolean;
  renderProfile?: Diagonal.Pages.Enums.PageRenderProfile;
}) {
  const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;

  // An embedded page normally drops the scroll viewport because the public
  // embed script resizes the iframe to fit the content instead. A draft
  // preview framed by the page editor is a fixed-height viewport with no such
  // resizing, so it keeps its own scroll container — the body is
  // overflow-hidden, which would otherwise leave the page unscrollable.
  const dropsScrollContainer =
    renderProfile === PageRenderProfile.Print || (isEmbedded && !isDraftPreview);

  return (
    <div
      // Page code is authored against the real viewport, so a custom layout sizes its
      // shell with `h-svh`/`min-h-screen` and scrolls internally. Those utilities
      // resolve against the viewport however this container is sized, leaving the
      // shell taller than the space the banner leaves it — which adds a second
      // scrollbar and pushes the shell's own footer below the fold. This attribute is
      // the hook `resources/css/page-scroll-container.css` uses to re-point them at the
      // banner-adjusted height, and it is only rendered while the container is active,
      // so embedded and print renders keep the real viewport.
      //
      // That override has to live in unlayered CSS rather than in an arbitrary variant
      // here. A published workspace serves the utility it targets from the precompiled
      // workspace stylesheet's `tw-runtime` layer, which outranks this bundle's
      // `utilities` layer before specificity is considered, so a bundled utility can
      // never win. Unlayered author rules outrank every layer.
      //
      // Fixed descendants are exempt. They size against the viewport rather than being
      // laid out inside this container, so shortening them by the banner height leaves
      // a strip of the screen an overlay was meant to cover uncovered instead.
      data-page-scroll-container={!dropsScrollContainer ? '' : undefined}
      className={cn(
        !dropsScrollContainer && [
          // Each utility here has to stay a complete literal string. Tailwind's build-time
          // scanner reads this file as plain text, so composing a class name from a shared
          // constant drops it from the bundle with no build, type, or test error.
          '[--page-viewport-height:calc(100svh-var(--impersonation-banner-height,0px))]',
          'mt-[var(--impersonation-banner-height,0px)]',
          'h-(--page-viewport-height)',
          'overflow-y-auto'
        ]
      )}
    >
      {children}
    </div>
  );
}
